import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';
import {
  actionTypes as resourceActionTypes,
} from 'redux-resource';
import {
  selection,
} from 'redux-resource-plugins';

import {
  actionTypes,
} from '../actions';
import {
  getFolderItems,
  getResourceById,
} from '../selectors';
import {
  throwError,
  getEntityType,
  getEntityLabel,
} from './helpers';
import {
  normalizeLibrary,
} from './proResources';
import api from '../api';

export default function* entityFlow(reportChannel) {
  function* fetchEntity({
    list,
    requestKey,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('read', {
      resourceType,
      requestKey: requestKey || 'fetchEntity',
    });

    try {
      yield eff.put(actions.pending());

      const resources = yield eff.call(
        api.getPersonalEntity,
        {
          type: getEntityType(resourceType),
        },
      );

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(resources);

      yield eff.put(actions.succeeded({
        list,
        resources,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);

      yield eff.call(throwError, e);
    }
  }

  function* addEntity({
    list,
    payload,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'createEntity',
    });

    try {
      yield eff.put(actions.pending());

      const response = yield eff.call(
        api.createEntity,
        {
          type: getEntityType(resourceType),
          body: payload,
        },
      );

      yield eff.put(actions.succeeded({
        resources: [response],
        list: list || 'all',
      }));

      if (resourceType !== 'tags') {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'success',
              key: 'item.added',
            },
          },
        );
      }

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(response);

      const eventKeys: any = {
        exercises: 'pro_created_exercise',
        warmups: 'created_warmup_template',
        cooldowns: 'created_cooldown_template',
        workoutTemplates: 'pro_created_workout',
        programTemplates: 'pro_created_program',
        products: 'pro_created_product',
        productPlans: 'pro_created_product_plan',
      };

      if (eventKeys[resourceType]) {
        yield eff.put({
          type: 'GC_ANALYTICS',
          eventKey: eventKeys[resourceType],
          attrs: {
            id: response.id,
            name: response.name,
          },
        });
      }
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);

      yield eff.call(throwError, e);
    }
  }

  function* updateEntity({
    id,
    payload,
    endNotify,
    requestType,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('update', {
      resourceType,
      resources: [id],
      requestKey: 'updateEntity',
    });

    yield eff.put(actions.pending());

    try {
      const response = yield eff.call(
        api.updateEntity,
        {
          id,
          type: getEntityType(requestType || resourceType),
          body: payload,
        },
      );

      yield eff.put(actions.succeeded({
        resources: [response],
      }));

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(response);

      if (endNotify) {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'success',
              key: 'changes.saved',
            },
          },
        );
      }
    } catch (e) {
      if (endNotify) {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'error',
              key: 'changes.not_saved',
            },
          },
        );
      }
      yield eff.put(actions.failed({
        resources: [],
      }));
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);

      yield eff.call(throwError, e);
    }
  }

  function* deleteEntity({
    id,
    qs,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('delete', {
      resourceType,
      resources: [id],
      requestKey: 'deleteEntity',
    });

    try {
      yield eff.put(actions.pending());

      yield eff.call(
        api.deleteEntity,
        {
          id,
          qs,
          type: getEntityType(resourceType),
        },
      );

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(id);

      yield eff.put(actions.succeeded({
        resources: [id],
      }));

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: resourceType === 'folders' ? 'folder.deleted' : 'item.deleted',
          },
        },
      );
    } catch (e) {
      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'error',
            key: 'item.not_deleted',
          },
        },
      );
      yield eff.put(actions.failed({
        resources: [],
      }));
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);

      yield eff.call(throwError, e);
    }
  }

  function* getFullEntity({
    id,
    requestType,
    resourceType,
  }) {
    const actions = createActionCreators('read', {
      resourceType,
      requestKey: 'getEntity',
    });

    try {
      yield eff.put(actions.pending({
        resources: [id],
      }));

      const response = yield eff.call(
        api.getEntity,
        {
          id,
          type: getEntityType(requestType || resourceType),
        },
      );

      yield eff.put(actions.succeeded({
        resources: [response],
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* addFolder({
    payload,
    callbacks,
  }) {
    const actions = createActionCreators('create', {
      resourceType: 'folders',
      requestKey: 'createFolder',
    });

    try {
      yield eff.put(actions.pending());

      const response = yield eff.call(
        api.createFolder,
        {
          body: payload,
        },
      );

      yield eff.put(actions.succeeded({
        resources: [response],
      }));

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'folder.added',
          },
        },
      );

      callbacks.setSubmitting?.(false);
      callbacks.onSuccess?.(response);

      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: 'created_folder',
        attrs: {
          name: response.name,
        },
      });
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      if (callbacks?.setSubmitting) {
        callbacks.setSubmitting(false);
      }
      yield eff.call(throwError, e);
    }
  }

  function* updateFolder({
    id,
    payload,
    callbacks,
  }) {
    const actions = createActionCreators('update', {
      resourceType: 'folders',
      requestKey: 'updateFolder',
    });

    try {
      yield eff.put(actions.pending());
      const response = yield eff.call(
        api.updateFolder,
        {
          id,
          body: payload,
        },
      );

      yield eff.put(actions.succeeded({
        resources: [response],
      }));

      if (callbacks?.setSubmitting) {
        callbacks.setSubmitting(false);
      }

      if (callbacks?.onSuccess) {
        callbacks.onSuccess();
      }
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      if (callbacks?.setSubmitting) {
        callbacks.setSubmitting(false);
      }
      yield eff.call(throwError, e);
    }
  }

  function* moveToFolder({
    items,
    targetId,
    resourceType,
    callbacks,
  }) {
    const target = yield eff.select(
      getResourceById(
        'folders',
        +targetId,
      ),
    );

    function* update(item) {
      try {
        const result = yield eff.call(
          (item.parent_id) ? api.updateFolder : api.updateEntity,
          {
            id: item.id,
            type: getEntityType(resourceType),
            body: item.parent_id
              ? {
                name: item.name,
                parent_id: targetId,
              } : {
                folder_id: targetId,
              },
          },
        );

        return result;
      } catch (e) {
        yield eff.call(throwError, e);

        return null;
      }
    }

    try {
      const results = yield eff.all(
        items.map(item => eff.call(update, item)),
      );

      const {
        folders,
        resources,
      } = results
        .filter(item => item && item.id)
        .reduce((acc, item) => (item.parent_id
          ? {
            ...acc,
            folders: [
              ...acc.folders,
              item,
            ],
          }
          : {
            ...acc,
            resources: [
              ...acc.resources,
              item,
            ],
          }), {
          folders: [],
          resources: [],
        });

      if (folders.length) {
        yield eff.put({
          type: resourceActionTypes.UPDATE_RESOURCES_SUCCEEDED,
          resourceType: 'folders',
          resources: folders,
        });
      }

      yield eff.put({
        type: resourceActionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resourceType,
        resources,
      });

      yield eff.put(
        selection.clearSelectedResources(resourceType),
      );
      yield eff.put(
        selection.clearSelectedResources('folders'),
      );

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: items.length === 1 ? {
            type: 'success',
            key: 'item.moved',
            params: {
              item: items[0].name,
              destination: target.name,
            },
          } : {
            type: 'success',
            key: 'items.moved',
            params: {
              type: getEntityLabel(resourceType),
              destination: target.name,
            },
          },
        },
      );

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.();
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.();
      yield eff.call(throwError, e);
    }
  }

  function* duplicateEntity({
    ids,
    folderIds,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'duplicateEntity',
    });

    try {
      yield eff.put(actions.pending());
      const result = yield eff.call(
        api.duplicateEntity,
        {
          type: getEntityType(resourceType),
          body: {
            ids,
            folder_ids: folderIds,
          },
        },
      );

      yield eff.put(actions.succeeded({
        resources: result,
        list: 'library',
      }));

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(result);

      const notifyKeys: any = {
        warmups: 'program_workout.saved',
        personalWarmups: 'program_workout.saved',
        cooldowns: 'program_workout.saved',
        personalCooldowns: 'program_workout.saved',
        workoutTemplates: 'program_workout.saved',
        personalWorkouts: 'program_workout.saved',
      };

      if (notifyKeys[resourceType]) {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'success',
              key: notifyKeys[resourceType],
            },
          },
        );
      }

      const eventKeys: any = {
        warmups: 'created_warmup_template',
        personalWarmups: 'created_warmup_template',
        cooldowns: 'created_cooldown_template',
        personalCooldowns: 'created_cooldown_template',
      };

      if (eventKeys[resourceType]) {
        yield eff.put({
          type: 'GC_ANALYTICS',
          eventKey: eventKeys[resourceType],
          attrs: {
            id: result.id,
            name: result.name,
          },
        });
      }
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* deleteItems({
    resources,
    resourceType,
    callbacks,
  }) {
    let calls: any = [];
    let puts: any = [];
    const getCallEffects = (ids: number[], type: string): any[] => ids.map(
      id => eff.call(
        api.deleteEntity,
        {
          id,
          type: getEntityType(type),
        },
      ),
    );
    const getPutEffects = (ids: number[], type: string): any[] => eff.put({
      type: resourceActionTypes.DELETE_RESOURCES_SUCCEEDED,
      resourceType: type,
      resources: ids,
    });

    try {
      if (!Array.isArray(resources) && typeof resources === 'object') {
        calls = Object
          .keys(resources)
          .reduce(
            (acc, type) => [
              ...acc,
              ...getCallEffects(resources[type], type),
            ], [],
          );

        puts = Object
          .keys(resources)
          .map(
            type => getPutEffects(resources[type], type),
          );
      } else {
        calls = getCallEffects(resources, resourceType);
        puts = [getPutEffects(resources, resourceType)];
      }
      const results = yield eff.all(calls);

      yield eff.all(puts);

      callbacks?.onSuccess?.(results);
    } catch (e) {
      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  function* updateItems({ resources, resourceType, callbacks }) {
    let calls: any = [];
    let puts: any = [];
    const getCallEffects = (items: any[], type: string): any[] => items.map(
      ({ id, ...body }) => eff.call(
        api.updateEntity,
        {
          id,
          body,
          type: getEntityType(type),
        },
      ),
    );
    const getPutEffects = (items: any[], type: string): any[] => eff.put({
      type: resourceActionTypes.UPDATE_RESOURCES_SUCCEEDED,
      resources: items,
      resourceType: type,
    });

    try {
      if (!Array.isArray(resources) && typeof resources === 'object') {
        calls = Object
          .keys(resources)
          .reduce(
            (acc, type) => [
              ...acc,
              ...getCallEffects(resources[type], type),
            ], [],
          );

        puts = Object
          .keys(resources)
          .map(
            type => getPutEffects(resources[type], type),
          );
      } else {
        calls = getCallEffects(resources, resourceType);
        puts = [getPutEffects(resources, resourceType)];
      }
      const results = yield eff.all(calls);

      callbacks?.onSuccess?.(results);

      yield eff.all(puts);
    } catch (e) {
      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  function* copyItems({ items, folderId, resourceType }) {
    const actions = createActionCreators('create', {
      resourceType: 'folders',
      requestKey: 'copyItems',
    });

    yield eff.put(
      actions.pending(),
    );

    try {
      const {
        ids,
        folderIds,
      } = items.reduce((acc, item) => (item.parent_id
        ? {
          ...acc,
          folderIds: [
            ...acc.folderIds,
            item.id,
          ],
        }
        : {
          ...acc,
          ids: [
            ...acc.ids,
            item.id,
          ],
        }), {
        ids: [],
        folderIds: [],
      });
      const type = getEntityType(resourceType);

      if (folderIds.length) {
        yield eff.all(
          folderIds.map(
            id => eff.call(
              api.duplicateEntityItem,
              {
                id,
                type: 'folders',
              },
            ),
          ),
        );
      }

      const results = ids.length
        ? yield eff.call(
          api.duplicateEntity,
          {
            type,
            body: {
              ids,
              folder_ids: [folderId],
            },
          },
        )
        : [];

      const all = yield eff.select(
        getFolderItems(resourceType, folderId),
      );

      const cutName = name => (name.indexOf('(') !== -1
        ? name.substring(0, name.indexOf('('))
        : name);

      // change name?
      yield eff.all(
        results.map(({ id, name }) => eff.call(
          api.updateEntity,
          {
            id,
            type,
            body: {
              name: `${name}(${all.filter(
                a => cutName(a.name) === cutName(name),
              ).length})`,
            },
          },
        )),
      );

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'success',
            key: 'item.copied',
          },
        },
      );

      const lib = yield eff.call(api.getLibrary);
      const normalized = normalizeLibrary(lib);

      yield eff.put(
        actions.succeeded(normalized),
      );
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* saveAsTemplate({
    id,
    payload,
    resourceType,
    callbacks,
  }) {
    const requestType = {
      workoutTemplates: 'program_workouts',
      personalWorkouts: 'personal_workouts',
      warmups: 'personal_workouts',
      cooldowns: 'personal_workouts',
      programTemplates: 'personal_programs',
    }[resourceType];

    const saveType = {
      personalWorkouts: 'workoutTemplates',
    }[resourceType] || resourceType;

    const actions = createActionCreators('create', {
      resourceType: saveType,
      requestKey: 'saveAsTemplate',
      list: 'library',
    });

    yield eff.put(
      actions.pending(),
    );

    try {
      const result = yield eff.call(
        api.saveAsTemplate,
        {
          id,
          body: payload,
          type: requestType,
        },
      );

      yield eff.put(actions.succeeded({
        resources: [result],
      }));

      callbacks?.onSuccess?.(result);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  yield eff.takeEvery(actionTypes.REQUEST_ADD_ENTITY, addEntity);
  yield eff.takeEvery(actionTypes.REQUEST_FETCH_ENTITY, fetchEntity);
  yield eff.takeEvery(actionTypes.REQUEST_UPDATE_ENTITY, updateEntity);
  yield eff.takeEvery(actionTypes.REQUEST_DELETE_ENTITY, deleteEntity);
  yield eff.takeEvery(actionTypes.REQUEST_FULL_ENTITY, getFullEntity);
  yield eff.takeEvery(actionTypes.REQUEST_ADD_FOLDER, addFolder);
  yield eff.takeEvery(actionTypes.REQUEST_UPDATE_FOLDER, updateFolder);
  yield eff.takeEvery(actionTypes.REQUEST_MOVE_TO_FOLDER, moveToFolder);
  yield eff.takeEvery(actionTypes.REQUEST_DUPLICATE_ENTITY, duplicateEntity);
  yield eff.takeEvery(actionTypes.REQUEST_DELETE_ITEMS, deleteItems);
  yield eff.takeEvery(actionTypes.REQUEST_UPDATE_ITEMS, updateItems);
  yield eff.takeEvery(actionTypes.REQUEST_COPY_ITEMS, copyItems);
  yield eff.takeEvery(actionTypes.REQUEST_SAVE_AS_TEMPLATE, saveAsTemplate);
}
